import { Injectable } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Events } from '@df/core/service/event';
import { NBuilderConfig } from '../common/builder.config';
import { NBuilderProductsSetup } from '../common/products-setup/builder-products-setup.config';
import { NOutfitGeneralSetup } from './general-setup/outfit-general-setup.config';
import { NOutfitBuilder } from './outfit-builder.namespace';

@Injectable({
    providedIn: 'root'
})
export class OutfitBuilderService {
    constructor(private productResource: ProductResource, private events: Events) {}
    getHorseProducts(data: NOutfitBuilder.IStorageData, sectionOptionId?: number): Product<IProductDataV2>[] {
        return this.productResource
            .getAllExisting()
            .filter(
                (product: Product<IProductDataV2>) =>
                    this.isBuilderProduct(product, sectionOptionId) &&
                    (Array.isArray(product.data.horse_type)
                        ? product.data.horse_type.includes(data.horse.tabId)
                        : product.data.horse_type === data.horse.tabId)
            ) as Product<IProductDataV2>[];
    }
    getRiderProducts(data: NOutfitBuilder.IStorageData, sectionOptionId?: number): Product<IProductDataV2>[] {
        return this.productResource
            .getAllExisting()
            .filter(
                (product: Product<IProductDataV2>) =>
                    this.isBuilderProduct(product, sectionOptionId) &&
                    (Array.isArray(product.data.gender)
                        ? product.data.gender.includes(data.rider.tabId)
                        : product.data.gender === data.rider.tabId)
            ) as Product<IProductDataV2>[];
    }
    loadAllProducts() {
        return this.productResource.find({
            filter: { eligible_builder: 1 },
            verbosity: 2
        });
    }
    removeRedundantData(data: NOutfitBuilder.IStorageData): void {
        switch (data.step) {
            case NOutfitBuilder.ESteps.configure:
                data.horse = undefined;
                data.rider = undefined;
                this.resetProductStep(data);
                break;
            case NOutfitBuilder.ESteps.rider:
                data.horse = undefined;
                this.resetProductStep(data);
                break;
            case NOutfitBuilder.ESteps.horse:
                this.resetProductStep(data);
        }
    }
    validateData(data: NOutfitBuilder.IStorageData, config: NOutfitGeneralSetup.IGeneralSetupData): boolean {
        const steps = NOutfitBuilder.STEPS_CONFIG[data.configOption];
        const index = steps.findIndex(e => e === data.step);
        return index >= 0 ? !steps.slice(0, index).find(s => !this.isStepValid(s, data, config)) : false;
    }
    goToStep(data: NOutfitBuilder.IStorageData, step: NOutfitBuilder.ESteps): void {
        data.step = step;
        this.removeRedundantData(data);
        this.events.broadcast(NBuilderConfig.updateStorageEvent);
    }
    getPrevStep(data: NOutfitBuilder.IStorageData): NOutfitBuilder.ESteps {
        const steps = NOutfitBuilder.STEPS_CONFIG[data.configOption];
        const indexOfCurrentStep = steps.findIndex(e => e === data.step);
        return steps[Math.max(0, indexOfCurrentStep - 1)];
    }
    private isBuilderProduct(product: Product<IProductDataV2>, sectionOptionId?: number): boolean {
        return (
            product.data.eligible_builder &&
            (!sectionOptionId || this.productHasSectionOption(product, sectionOptionId)) &&
            (product.isConfigurable ? !!product.children.find(p => p.isSalable()) : product.isSalable())
        );
    }
    private isStepValid(
        step: NOutfitBuilder.ESteps,
        data: NOutfitBuilder.IStorageData,
        config: NOutfitGeneralSetup.IGeneralSetupData
    ): boolean {
        switch (step) {
            case NOutfitBuilder.ESteps.configure:
                return data.configOption !== undefined;
            case NOutfitBuilder.ESteps.rider:
                return !!data.rider.optionName && !!data.rider.tabId && this.checkRiderOptionExists(data, config);
            case NOutfitBuilder.ESteps.horse:
                return !!data.horse.optionName && !!data.horse.tabId && this.checkHorseOptionExists(data, config);
        }
        return false;
    }
    private checkRiderOptionExists(data: NOutfitBuilder.IStorageData, config: NOutfitGeneralSetup.IGeneralSetupData): boolean {
        switch (data.rider.tabId) {
            case NOutfitGeneralSetup.EGender.men:
                return !!config.riderStepMenOptions.find(o => o.name === data.rider.optionName);
            case NOutfitGeneralSetup.EGender.women:
                return !!config.riderStepWomenOptions.find(o => o.name === data.rider.optionName);
        }
        return false;
    }
    private checkHorseOptionExists(data: NOutfitBuilder.IStorageData, config: NOutfitGeneralSetup.IGeneralSetupData): boolean {
        switch (data.horse.tabId) {
            case NOutfitGeneralSetup.EHorseType.horse:
                return !!config.horseStepHorseOptions.find(o => o.name === data.horse.optionName);
            case NOutfitGeneralSetup.EHorseType.pony:
                return !!config.horseStepPonyOptions.find(o => o.name === data.horse.optionName);
        }
        return false;
    }
    private productHasSectionOption(product: Product<IProductDataV2>, optionId: number): boolean {
        return product.getAttr<number[]>(NBuilderProductsSetup.SECTION_OPTION_ATTR_ID)?.includes(optionId);
    }
    private resetProductStep(data: NOutfitBuilder.IStorageData): void {
        data.optionToggleState = undefined;
        data.currentSectionOptionId = undefined;
        data.selection = {};
    }
}
